/**
 * Export document to documentation: API call → update issue in DB (add linkedDocumentation) → optional document delete → broadcast.
 *
 * Przy dodawaniu (analogicznie do addDocument / addDocumentation):
 * - addDocument: addDocument(entity) → broadcastCreatedDocument → addRequest (kolejka POST)
 * - addDocumentation: addDocumentation(entity) → broadcastDocumentationSaved → addRequest
 * Tu: getOne(issue) → updateOne(issueId, { linkedDocumentation: [...existing, newItem] }) → broadcast issue (getIssue).
 *
 * Przy usuwaniu dokumentu – jak w deleteDocument.ts: getOne → hard delete lub soft delete + addRequest → broadcastRemovedDocument.
 */
import {
  SyncStatus,
  isSyncStatusPending,
} from 'shared/domain/entitySyncStatus/syncStatus';
import {
  HttpRequestModelType,
  HttpRequestStatus,
} from 'shared/domain/httpRequest/httpRequestModel';
import {
  createLinkedDocumentationItem,
  LinkedDocumentationItem,
  LinkedDocumentationType,
} from 'shared/domain/issue/linkedDocumentation';
import { directoriesSync } from '../../../directories';
import { documentationsSync } from '../../../documentations';
import { getNewDocumentationName } from 'shared/domain/issue/mapping/exportDocument/toOut/createExportDocumentOutDto';
import { upsertExportedDocumentationAfterExport } from '../../../documentations/upsertExportedDocumentationAfterExport';
import { debugLog } from 'shared/logger/debugLog';
import { HttpError } from 'serviceWorker/helpers/httpError';
import { requestProjectApi } from 'serviceWorker/api/requestApi';
import { captureSelectedProjectScope, type DataScope } from 'serviceWorker/helpers/dataScope';
import { broadcastRemovedDocument } from 'serviceWorker/broadcasts/documents';
import {
  getOne as getDocument,
  getOneByRemote as getDocumentByRemote,
  removeBatch as deleteDocuments,
  updateOne as updateDocument,
} from 'serviceWorker/db/documents';
import { add as addRequest } from 'serviceWorker/db/httpRequests';
import { resolveExtensionFromFileName } from 'shared/utils/fileExtensions';
import {
  announceLinkedDocumentationChange,
  appendLinkedDocumentationToIssueRecord,
} from '../model';
import { normalizeExportToDocumentationResult } from '../normalizeExportToDocumentationResult';
import { saveExportedVersionSettings } from './exportedVersionFollowUps';
import type {
  ExportToDocumentationRequest,
  ExportToDocumentationResultData,
} from 'shared/domain/issue/linkedDocumentation/operations';

const DRAWING_SYNC_WAIT_TIMEOUT_MS = 10_000;
const DRAWING_SYNC_WAIT_INTERVAL_MS = 250;

type ExportPayload = ExportToDocumentationRequest['payload'];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function exportToDocumentationRequest(
  issueId: string,
  documentId: string,
  payload: ExportPayload
): Promise<ExportToDocumentationResultData> {
  const exported = await requestProjectApi<unknown>({
    method: 'POST',
    path: `issue/${issueId}/document/${documentId}/exportToDocumentation`,
    body: payload,
  });
  return normalizeExportToDocumentationResult(exported);
}

async function appendLinkedDocumentationToIssue(
  issueId: string,
  documentId: string,
  localId: number | undefined,
  preferredImageRotation: number | undefined,
  payload: ExportPayload,
  data: ExportToDocumentationResultData
): Promise<LinkedDocumentationItem[] | undefined> {
  const documentByRemoteId = documentId
    ? await getDocumentByRemote(documentId)
    : undefined;
  const documentByLocalId =
    localId !== undefined ? await getDocument(localId) : undefined;
  const sourceDoc = documentByLocalId ?? documentByRemoteId;

  // The export links the issue to the exported version (PT-7117); the backend saves the link itself.
  const newItem: LinkedDocumentationItem = createLinkedDocumentationItem({
    type: LinkedDocumentationType.version,
    documentationId: data.documentationId,
    versionId: data.versionId,
    sourceDocumentId: documentId,
    nameCandidates: [
      data.documentationName,
      getNewDocumentationName(payload),
      sourceDoc?.title,
      sourceDoc?.data?.name,
      documentByRemoteId?.title,
      documentByRemoteId?.data?.name,
    ],
    descriptionCandidates: [
      sourceDoc?.description,
      documentByRemoteId?.description,
    ],
    imageRotationCandidates: [preferredImageRotation, sourceDoc?.imageRotation],
  });

  return appendLinkedDocumentationToIssueRecord(issueId, newItem);
}

/**
 * Removes the exported document: locally while the scope is current, on the
 * server (queued) in the project of the export.
 */
async function removeSourceDocumentAfterExport(
  localId: number | undefined,
  scope: DataScope
): Promise<void> {
  if (localId === undefined) {
    return;
  }

  const document = await getDocument(localId);
  if (!document) {
    return;
  }

  if (!document._id) {
    const [removedId] = (await scope.write(() => deleteDocuments([localId]))) ?? [];
    if (removedId !== undefined) {
      broadcastRemovedDocument(removedId);
    }
    return;
  }

  await scope.write(() =>
    updateDocument(localId, {
      deleted: true,
      syncStatus: SyncStatus.PENDING_DELETE,
    })
  );
  if (scope.projectId) {
    await addRequest({
      createdAt: Date.now(),
      entityType: HttpRequestModelType.document,
      data: {
        localId,
        _id: document._id,
        projectId: scope.projectId,
      },
      method: 'DELETE',
      status: HttpRequestStatus.NEW,
    });
  }
  await scope.write(async () => broadcastRemovedDocument(localId));
}

async function dispatchDocumentationSyncEvents(): Promise<void> {
  await documentationsSync.syncAndWait();
  directoriesSync.sync();
}

/**
 * Exports the issue's document to the documentation and links it to the issue;
 * resolves once both are saved (the documentation sync follows in the
 * background), rejects when the server refused.
 */
export async function exportDocumentToDocumentation(
  detail: ExportToDocumentationRequest
): Promise<ExportToDocumentationResultData> {
  const { issueId, documentId, payload, version, localId, imageRotation } = detail;
  const scope = await captureSelectedProjectScope();

  if (localId !== undefined) {
    await waitForDrawingSyncIfNeeded(localId);
  }

  const data = await exportToDocumentationRequest(issueId, documentId, payload).catch((error: unknown) => {
    if (error instanceof HttpError && error.responseBody) {
      debugLog('exportDocumentToDocumentation response body', error.responseBody, payload);
    }
    throw error;
  });
  // Server follow-ups go to the project of the export; local writes only while it is current.
  const linkedDocumentation = await scope.write(() =>
    appendLinkedDocumentationToIssue(issueId, documentId, localId, imageRotation, payload, data)
  );
  await scope.write(() =>
    upsertExportedDocumentationAfterExport(data, payload, {
      imageRotation,
      preferredExtension: resolveExtensionFromFileName(data.documentationName ?? getNewDocumentationName(payload) ?? ''),
    })
  );
  // After the documentation is stored on the device: making the version current changes it there.
  const unsavedVersionSettings = await saveExportedVersionSettings(data, version, scope);
  // The source document is removed only once the link replacing it is saved: an export cut by a
  // project switch leaves the document on the issue rather than losing it.
  if (linkedDocumentation) {
    await removeSourceDocumentAfterExport(localId, scope);
  }
  await scope.write(async () => {
    await announceLinkedDocumentationChange(issueId);

    dispatchDocumentationSyncEvents().catch((enrichmentError: unknown) => {
      debugLog(
        'exportDocumentToDocumentation enrichment failed after critical success',
        enrichmentError
      );
    });
  });
  return unsavedVersionSettings.length > 0 ? { ...data, unsavedVersionSettings } : data;
}

function shouldWaitForDrawingSync(document: {
  _id?: string;
  drawingSyncStatus?: SyncStatus;
  data?: { isDrawn?: boolean } | null;
} | undefined): boolean {
  if (!document?._id) {
    return false;
  }

  return (
    Boolean(document.data?.isDrawn) &&
    isSyncStatusPending(document.drawingSyncStatus)
  );
}

async function waitForDrawingSyncIfNeeded(localId: number): Promise<void> {
  let document = await getDocument(localId).catch(() => undefined);

  if (!shouldWaitForDrawingSync(document)) {
    return;
  }

  const startedAt = Date.now();

  while (
    document &&
    shouldWaitForDrawingSync(document) &&
    Date.now() - startedAt < DRAWING_SYNC_WAIT_TIMEOUT_MS
  ) {
    await sleep(DRAWING_SYNC_WAIT_INTERVAL_MS);

    document = await getDocument(localId).catch(() => undefined);
  }

  if (document && shouldWaitForDrawingSync(document)) {
    debugLog(
      '[exportDocumentToDocumentation] waitForDrawingSync timeout',
      {
        localId,
        timeoutMs: DRAWING_SYNC_WAIT_TIMEOUT_MS,
        drawingSyncStatus: document.drawingSyncStatus,
        isDrawn: document.data?.isDrawn,
      }
    );
    throw new Error('Drawing sync timeout - please wait for drawing to finish uploading');
  }
}
